package com.trevorwiebe.timesheet.admin.reporting.presentation.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.admin.reporting.presentation.MissingStartEndFixState
import com.trevorwiebe.timesheet.admin.reporting.presentation.ReportingEvent
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.formatWithCommas
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.displayLabel

@Composable
fun MissingStartEndFixDialog(
    fix: MissingStartEndFixState,
    onEvent: (ReportingEvent) -> Unit,
) {
    Dialog(onDismissRequest = { onEvent(ReportingEvent.OnDismissFixDialog) }) {
        Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                Text(
                    text = "Fix Day Boundary",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "Truck ${fix.boundary.unitNumber} • ${boundaryLabel(fix)}",
                    fontSize = 14.sp,
                )
                Spacer(modifier = Modifier.height(12.dp))

                if (fix.entriesBefore.isNotEmpty()) {
                    ContextLabel("Before the boundary")
                    fix.entriesBefore.forEach { ContextEntryRow(it) }
                    Spacer(modifier = Modifier.height(12.dp))
                }

                if (fix.endTemplate != null) {
                    TimesheetTextField(
                        modifier = Modifier.fillMaxWidth(),
                        text = fix.mileageText,
                        label = "End of day odometer",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        onTextChange = { onEvent(ReportingEvent.OnFixMileageChanged(it)) },
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                }
                if (fix.startTemplate != null) {
                    TimesheetTextField(
                        modifier = Modifier.fillMaxWidth(),
                        text = fix.mileageText,
                        label = "Start of day odometer",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        onTextChange = { onEvent(ReportingEvent.OnFixMileageChanged(it)) },
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                }
                TimesheetTextField(
                    modifier = Modifier.fillMaxWidth(),
                    text = fix.locationText,
                    label = "Location",
                    singleLine = true,
                    onTextChange = { onEvent(ReportingEvent.OnFixLocationChanged(it)) },
                )

                if (fix.entriesAfter.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(12.dp))
                    ContextLabel("After the boundary")
                    fix.entriesAfter.forEach { ContextEntryRow(it) }
                }

                if (fix.error != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = fix.error ?: "",
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
                val warnDifference = fix.warnDifference
                if (warnDifference != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = "This is ${warnDifference.formatWithCommas()} miles more than the " +
                            "previous entry for this truck. Save anyway?",
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
                Spacer(modifier = Modifier.height(16.dp))
                DialogButtonRow(
                    confirmButtonText = if (warnDifference != null) "Save Anyway" else "Save",
                    onConfirmClick = {
                        onEvent(
                            if (warnDifference != null) {
                                ReportingEvent.OnConfirmFixWarning
                            } else {
                                ReportingEvent.OnSaveFix
                            }
                        )
                    },
                    confirmEnabled = !fix.isSaving,
                    dismissButtonText = "Cancel",
                    onDismissClick = { onEvent(ReportingEvent.OnDismissFixDialog) },
                )
            }
        }
    }
}

private fun boundaryLabel(fix: MissingStartEndFixState): String {
    val end = fix.boundary.endDate?.let { Util.toFriendlyDate(it) }
    val start = fix.boundary.startDate?.let { Util.toFriendlyDate(it) }
    return when {
        end != null && start != null -> "$end → $start"
        end != null -> "end of $end"
        else -> "start of ${start.orEmpty()}"
    }
}

@Composable
private fun ContextLabel(text: String) {
    Text(
        text = text,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
    )
}

@Composable
private fun ContextEntryRow(entry: MileageEntry) {
    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp)) {
        Text(
            text = Util.toFriendlyDateTime(entry.date),
            modifier = Modifier.weight(1.4f),
            fontSize = 13.sp,
        )
        Text(
            text = entry.tripType.displayLabel(),
            modifier = Modifier.weight(1f),
            fontSize = 13.sp,
        )
        Text(
            text = entry.mileageValue.formatWithCommas(),
            modifier = Modifier.weight(0.8f),
            fontSize = 13.sp,
            textAlign = TextAlign.End,
        )
    }
}
